import {
  cloneElement,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  ControlBarButton,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPages,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTrigger,
  Menu,
  Icon,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuShortcut,
  MenuTrigger,
  OptionPanelActionRow,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { IssueScope } from "#app/lib/filters/issue-scope";
import type {
  FilterConfig,
  QuickFilterLayerFor,
  UseFiltersResult,
} from "#app/lib/filters/types";
import { hasQuickFilterValue } from "#app/lib/filters/quick-values";
import { useCloseOnNavigate } from "#app/lib/hooks/use-close-on-navigate";
import { isTextEditingTarget } from "#app/lib/utils/typing-target";
import {
  CLEAR_FILTERS_SHORTCUT,
  CLEAR_FILTERS_SHORTCUT_LABEL,
  FILTER_PANEL_SHORTCUT,
  useFilterHotkeyTarget,
} from "#app/lib/hooks/use-filter-hotkeys";
import { AdvancedFilters } from "./advanced-filters";
import {
  FilterModeHeader,
  FilterModeTabs,
  FilterSaveButton,
  SavedFiltersToggle,
} from "./filter-mode-tabs";
import { FilterToggle } from "./filter-toggle";
import { QuickFilterMenu } from "./quick-filter-menu";
import { QuickFilters } from "./quick-filters";
import {
  SavedFilterNameDialog,
  SavedFiltersMenuPage,
  SavedFiltersSection,
  savedFilterDialogKey,
  type SavedFilterDialogIntent,
  type SavedFiltersProps,
} from "./saved-filters";
import type { SavedFiltersTab } from "./filter-mode-tabs";
import { useSavedFilters } from "#app/lib/hooks/use-saved-filters";
import { AnalyticsContext, IssueEvent, useAnalytics } from "#app/lib/analytics";
import {
  type FilterClearVia,
  useIssueFiltersOptional,
} from "#app/lib/filters/issue-filter-context";

type FilterPanelProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: FilterConfig;
  filters: UseFiltersResult;
  /** The control the panel hangs off — the toolbar chip (`FilterToggle`) or
   *  the map dock's `ControlBarButton`. The panel renders it, so mount this
   *  component beside the cluster the control belongs to, not up in the
   *  route. It is a real trigger on both viewports: Base UI renders it in
   *  place with the trigger's handlers merged in, so a second press closes
   *  the panel and a joined bar keeps its geometry. */
  trigger: ReactElement<Record<string, unknown>>;
  /** The screen's scope, so both editors drop what the scope has already
   *  answered and offer only what it can still narrow. */
  scope?: IssueScope;
  /** Filters the VIEW carries, which this panel does not edit — the host
   *  passes it while the view has any. The panel's foot names them and opens
   *  the view's own editor, so a chip counting more than the rows show has
   *  the rest accounted for. */
  viewFilters?: ViewFiltersNote;
  /** The ⌥⇧F chord's clear, where the chip's count spans more than this
   *  panel's pool — the host's both-layers clear. Defaults to the pool's. */
  onClearAll?: (via?: FilterClearVia) => void;
  /** The layer each quick field lives in — see `QuickFilterLayerFor`. A
   *  field routed elsewhere (the Assignee filter, onto the view) still reads
   *  and clears from this panel; only `filters` is the panel's own pool. */
  layerFor?: QuickFilterLayerFor;
}>;

export type ViewFiltersNote = {
  count: number;
  /** Opens the Customize view Filters page. */
  onEdit: () => void;
};

type FilterTab = "simple" | "advanced";

/** Wide enough for a row to carry its label AND its applied value, and for an
 *  advanced block's field line and operator/value line. FIXED, not a floor:
 *  a content-sized menu grows with whatever label an advanced row selects and
 *  the whole panel resizes under the pointer; the controls truncate instead.
 *  `MenuPopup` already caps itself at the available height and scrolls. */
const PANEL_WIDTH = 340;

/**
 * The filter panel: a `Menu` off the toolbar chip on desktop, a `Drawer` on
 * mobile. It always opens on the quick filters — see `handleOpenChange`.
 *
 * A MENU, because the filters open their values on HOVER (`QuickFilterMenu`) —
 * and hover only works with Base UI's submenu, whose grace area lets the
 * pointer travel diagonally into an open panel without the row it crosses
 * stealing it. It is also content-sized, which is what a side sheet was not:
 * Simple is seven rows, and a viewport-tall pane held them at the top of
 * several hundred pixels of nothing.
 *
 * There is no title bar. A menu says what it is by what it lists, and the
 * toolbar chip already carries the count. The one header rung is
 * `FilterModeHeader` — the Simple / Advanced strip with Save and the quiet
 * Saved toggle at its end — the same rung the Customize view Filters page
 * wears, so the two editors are picked the same way on every surface. No
 * clear rides it. It sits ABOVE the Simple body's search: the tabs choose the
 * editor, the search belongs to the editor it heads.
 *
 * The clear foots the panel instead, on a sticky footer rung that shows only
 * while the pool has something to clear. The toolbar chip's × disc and the
 * ⌥⇧F chord clear the pool too, but not every trigger has the disc — the
 * map's joined action carries its count as content, with nothing to press —
 * and a chord is not discoverable. The footer mirrors the header — pinned
 * under the scroller so it stays put however tall an Advanced build gets,
 * the hairline and the pinning are what say chrome — but its content is a
 * ROW, not a button: every rung in this panel spans the width (the header
 * strip, the search, the filter rows, the view-filters row), and a
 * right-aligned control would make the footer the one toolbar in a panel
 * that has none. The row is the rows' 32px rung in the view-filters row's
 * muted ink, found rather than noticed: a reset is cheap to undo and less
 * valuable than Save above it, so it is neither accent (Add Project's) nor
 * outline (the offer tier) — a full-width outline button was tried here
 * (2026-09-29) and reverted: its well clashed with the search input's
 * bordered well two rungs above it. (The phone sheet keeps its outline
 * button — a thumb needs the target, and the sheet's search is its own
 * band.) The ⌥⇧F chord rides the row's trailing edge as a `MenuShortcut`,
 * the way the account menu shows ⌥⇧Q — a menu row advertises its chord
 * inline, not in a tooltip — and only while this panel owns it. It clears
 * THIS pool; a view's own filters are the separate in-flow row above it,
 * edited elsewhere. The menu stays open on press, so the rows are seen
 * resetting to "Any".
 *
 * The panel renders its own trigger, and the menu is never hung off an
 * `anchor` instead. A registered trigger is what gives a second press on the
 * chip a close (an anchored popup closes on the outside-press and the chip's
 * own press re-opens it in the same tick), and it is what lets the menu host
 * submenus at all: Base UI registers a menu's tree node only through its
 * trigger, so a triggerless root reads every submenu opening as a sibling and
 * closes itself the moment a row is hovered.
 */
export function FilterPanel({
  open,
  onOpenChange,
  config,
  filters,
  trigger,
  scope,
  viewFilters,
  onClearAll,
  layerFor: layerForProp,
}: FilterPanelProps) {
  const isMobile = useIsMobile();
  const { track } = useAnalytics();
  const analyticsScope = useIssueFiltersOptional()?.analyticsScope ?? "unknown";
  const layerFor: QuickFilterLayerFor = layerForProp ?? (() => filters);

  // The fields routed off this pool, and how many of them hold a value: the
  // panel's rows show them, so its count and its Clear cover them too.
  const routedDefs = (config.quickFilters ?? []).filter(
    (def) => layerFor(def.field) !== filters,
  );
  const routedCount = routedDefs.filter((def) =>
    hasQuickFilterValue(layerFor(def.field).quickValues[def.field]),
  ).length;
  const panelCount = filters.activeFilterCount + routedCount;
  // The footer's clear: this pool, plus the routed fields the rows show.
  const clearPanel = () => {
    if (panelCount > 0) {
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.ClearFilters,
        data: { count: panelCount, scope: analyticsScope, via: "panel_footer" },
      });
    }
    filters.clearAll();
    for (const def of routedDefs) layerFor(def.field).setQuickValue(def.field, null);
  };

  // The displayed tab IS filters.activeMode — the count/where derive from the
  // active mode, and features can switch it programmatically, so a separate
  // local tab state drifts: the badge counts one mode while the panel shows
  // the other's (empty) tab.
  const activeTab: FilterTab = filters.activeMode;

  // Destructured so the callbacks depend on the setter, not on the whole filter
  // object it happens to hang off.
  const { setActiveMode } = filters;

  // Advanced is a PLACE inside the panel, not a mode the panel keeps: closing
  // drops back to the quick filters, so the chip always opens on the rows that
  // state the applied values. Nothing built in the builder is lost on the way
  // back — `activeMode` picks the EDITOR and never the query, so both modes'
  // filters stay applied and the Advanced row keeps its count.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setActiveMode("simple");
        setSavedTabOpen(false);
      }
      onOpenChange(next);
    },
    [onOpenChange, setActiveMode],
  );

  // Dialog-system default: click-behind can navigate (workflow switch, module
  // rail), and the panel closes rather than outliving the page it filtered.
  useCloseOnNavigate(open, () => handleOpenChange(false));

  // The bare F opens this panel and ⌥⇧F clears its filters — whichever
  // mounted panel's trigger is furthest along in document order owns the
  // chords (a dialog's panel over the page's, same arbitration as the view
  // search's `/`). The trigger element is the arbitration point.
  const triggerRef = useRef<HTMLElement | null>(null);
  // The Simple page's search field; null while another page shows.
  const quickSearchRef = useRef<HTMLInputElement | null>(null);
  const isHotkeyTarget = useFilterHotkeyTarget({
    element: () => triggerRef.current,
    open: () => onOpenChange(true),
    clearAll: () => (onClearAll ? onClearAll("hotkey") : filters.clearAll()),
    hasActiveFilters: () =>
      filters.activeFilterCount + (viewFilters?.count ?? 0) > 0,
  });

  // The trigger carries the hotkey wiring: the ref for arbitration, and the
  // keycaps its tooltip advertises while this panel is the chords' current
  // target. Two trigger shapes take them under different names: the toolbar
  // chip (`FilterToggle`: `tooltipKeys` + the × disc's `clearTooltipKeys`)
  // and the map dock's joined action (`ControlBarButton`: `shortcut`).
  const chip = cloneElement(trigger, {
    ref: triggerRef,
    ...(trigger.type === FilterToggle
      ? {
          tooltipKeys: isHotkeyTarget ? FILTER_PANEL_SHORTCUT : undefined,
          clearTooltipKeys: isHotkeyTarget ? CLEAR_FILTERS_SHORTCUT : undefined,
        }
      : trigger.type === ControlBarButton
        ? { shortcut: isHotkeyTarget ? FILTER_PANEL_SHORTCUT : undefined }
        : null),
  });

  // The panel edits the toolbar's temporary pool only. A view's own filters
  // narrow the screen too and light the same chip, so the foot says how many
  // of the chip's count live on the view and where they are edited — the
  // one line here that opens a different surface.
  const viewFiltersLabel = viewFilters
    ? `${viewFilters.count} saved on this view`
    : null;

  // Saved filters: the viewer's personal snapshots for this category, the
  // same store the Customize view Filters page offers over a view's draft.
  // They open from the Saved toggle at the end of the header rung, a PLACE
  // in the panel rather than an editor mode: selecting it leaves both
  // editors' filters applied. Picking Simple or Advanced sets the mode and
  // leaves Saved. The name
  // dialog is rendered OUTSIDE the menu: the menu closes on the row press
  // that asks for it, so the dialog cannot live in its subtree.
  const savedFilterStore = useSavedFilters(config.category);
  const [savedTabOpen, setSavedTabOpen] = useState(false);
  const savedTab = useMemo<SavedFiltersTab>(
    () => ({
      selected: savedTabOpen,
      count: savedFilterStore.savedFilters.length,
      onSelectedChange: setSavedTabOpen,
      onSaveRequest: () => setDialogIntent({ kind: "create" }),
    }),
    [savedTabOpen, savedFilterStore.savedFilters.length],
  );
  const [dialogIntent, setDialogIntent] = useState<SavedFilterDialogIntent | null>(null);
  const savedFilters = useMemo<SavedFiltersProps>(
    () => ({
      store: savedFilterStore,
      onSaveRequest: () => setDialogIntent({ kind: "create" }),
      onRenameRequest: (row) => setDialogIntent({ kind: "rename", row }),
    }),
    [savedFilterStore],
  );
  // Keyed on the intent so each open seeds its own name (a rename starts
  // from the row's; a save starts blank) instead of inheriting the last.
  const saveDialog = (
    <SavedFilterNameDialog
      key={savedFilterDialogKey(dialogIntent)}
      intent={dialogIntent}
      onClose={() => setDialogIntent(null)}
      filters={filters}
      store={savedFilterStore}
      via="filter_panel"
    />
  );

  if (isMobile) {
    return (
      <>
        {saveDialog}
        <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        size="full"
      >
        <DrawerTrigger render={chip} />
        {/* Page fill, matching the project drawer — the filter inputs'
            ghost-surface treatment assumes it. */}
        <DrawerPopup showBar surface="background">
          {/* The stack the Simple tab's rows push their value pages onto;
              the header and panel below are its root page. */}
          <DrawerPages>
            <DrawerHeader>
              <Box direction="row" align="center" justify="between" gap="sm">
                {/* The title alone: the Simple tab's count and the rows'
                    own values already say what is active. */}
                <DrawerTitle>Filters</DrawerTitle>
                {/* The drawer has a title row, so Save rides it and the tabs
                    take the rung below on their own. */}
                <FilterSaveButton filters={filters} saved={savedTab} />
              </Box>
            </DrawerHeader>
            <DrawerPanel className="px-0 pt-3">
              {/* Mobile's drawer is not a menu — it has no hover and no
                  items — so it keeps a tab strip for the two modes, with the
                  Saved toggle on the strip's far end (Save rides the title
                  row above). */}
              <Box
                shrink
                border="bottom"
                paddingX="lg"
                paddingBottom="sm"
                direction="row"
                align="center"
                justify="between"
                gap="sm"
              >
                <FilterModeTabs
                  filters={filters}
                  saved={savedTab}
                  routedSimpleCount={routedCount}
                />
                <SavedFiltersToggle saved={savedTab} />
              </Box>
              {/* Touch has no hover, so the Simple tab's rows drill into a
                  page of the stack rather than opening a popup beside a
                  surface that reaches both screen edges. */}
              {savedTabOpen ? (
                <SavedFiltersSection config={config} filters={filters} {...savedFilters} />
              ) : activeTab === "simple" ? (
                <QuickFilters
                  config={config}
                  filters={filters}
                  scope={scope}
                  layerFor={layerFor}
                />
              ) : (
                <AdvancedFilters config={config} filters={filters} scope={scope} />
              )}
              {viewFilters && viewFiltersLabel ? (
                <Box border="top" paddingTop="sm" paddingX="md">
                  {/* On the rows' 24px content line: the row's own 8px inset
                      plus the 16px frame. */}
                  <OptionPanelActionRow
                    label={viewFiltersLabel}
                    icon="settings"
                    onClick={viewFilters.onEdit}
                  />
                </Box>
              ) : null}
            </DrawerPanel>
            {/* The clear foots the sheet, pinned under the scroller like the
                desktop menu's footer rung — but as a BUTTON, not a row. The
                menu's quiet row is found by a pointer scanning a short list;
                a thumb scanning a sheet needs the reset to stand out and to
                be a 40px target, so it takes the outline offer tier at full
                width, on the footer's hairline and safe-area inset. Only
                while there is something to clear. */}
            {panelCount > 0 ? (
              <DrawerFooter>
                <Button
                  variant="outline"
                  color="neutral"
                  size="xl"
                  fullWidth
                  onClick={clearPanel}
                >
                  <Icon name="x" />
                  Clear all filters
                </Button>
              </DrawerFooter>
            ) : null}
          </DrawerPages>
        </DrawerPopup>
        </Drawer>
      </>
    );
  }

  return (
    <>
      {saveDialog}
      <Menu open={open} onOpenChange={handleOpenChange}>
      <MenuTrigger render={chip} />
      {/* `align="end"` keeps the menu's right edge on the chip's, so it opens
          inward over the view rather than off the toolbar's end. */}
      {/* `sm` padding: the roomy 8px edge of the create-popover grammar, so
          the search field and rows land on the same insets as every other
          browsable popup. */}
      {/* Type to search: the menu takes focus as it opens (the F hotkey
          included), so a printable key would go to the rows' typeahead. On
          the Simple page it goes to "Filter by anything…" instead — focus
          moves during the keydown, so the character lands in the field. */}
      <MenuPopup
        align="end"
        minWidth={PANEL_WIDTH}
        padding="sm"
        onKeyDown={(event) => {
          const search = quickSearchRef.current;
          if (!search || isTextEditingTarget(event.target)) return;
          if (event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) return;
          if (event.key === " ") return;
          search.focus();
          event.preventBaseUIHandler();
        }}
      >
        {/* The header rung — tabs, Save and the Saved toggle — heads the
            menu, and the hairline under it is where the header ends and the
            body begins, whichever body is showing. Same inset as the search
            row below it, so the strip and the field share one left edge.

            Sticky: the popup's inner scroller caps at the viewport, and an
            Advanced build of several groups scrolls, so the rung pins to the
            top and the body scrolls under it. Negative margins pull it out to
            the scroller's edge (the popup's `sm` padding) so the pinned strip
            paints edge to edge with the popup's own surface, and the same
            padding is restored inside so the strip's content stays put.

            The sticky `top` is the same negative 8px, not 0: the scroller's
            padding insets the rectangle a sticky box pins against, so at
            `top: 0` the strip is held 8px BELOW where its negative margin
            placed it — and that shove lands on the air under its hairline,
            leaving the search field flush against the rule. */}
        <Box
          position="sticky"
          top={-8}
          zIndex={1}
          background="popover"
          rounded="lg"
          roundedSide="top"
          marginXNegative="sm"
          marginTopNegative="sm"
          paddingX="sm"
          paddingTop="sm"
        >
          <FilterModeHeader
            filters={filters}
            saved={savedTab}
            routedSimpleCount={routedCount}
          />
          <MenuSeparator />
        </Box>
        {savedTabOpen ? (
          <SavedFiltersMenuPage config={config} filters={filters} {...savedFilters} />
        ) : activeTab === "simple" ? (
          <QuickFilterMenu
            config={config}
            filters={filters}
            scope={scope}
            layerFor={layerFor}
            searchRef={quickSearchRef}
          />
        ) : (
          <AdvancedFilters config={config} filters={filters} scope={scope} />
        )}
        {viewFilters && viewFiltersLabel ? (
          <>
            <MenuSeparator />
            {/* `lg`: the Simple rows' 32px rung. Pressing it closes this
                menu — the Customize view panel retires it on open. */}
            <MenuItem size="lg" onClick={viewFilters.onEdit}>
              <Icon name="settings" size="sm" color="muted" />
              <Text size="sm" variant="muted">
                {viewFiltersLabel}
              </Text>
            </MenuItem>
          </>
        ) : null}
        {panelCount > 0 ? (
          /* The footer rung, the header's mirror: the same negative margins
             pull it to the scroller's edge and the same `-8` pins it where
             its margin placed it (see the header above). A MenuItem inside
             it keeps arrow-key reach; `closeOnClick={false}` leaves the menu
             open on press. */
          <Box
            position="sticky"
            bottom={-8}
            zIndex={1}
            background="popover"
            rounded="lg"
            roundedSide="bottom"
            marginXNegative="sm"
            marginBottomNegative="sm"
            paddingX="sm"
            paddingBottom="sm"
          >
            <MenuSeparator />
            <MenuItem
              closeOnClick={false}
              onClick={clearPanel}
              size="lg"
            >
              <Icon name="x" size="sm" color="muted" />
              <Text size="sm" variant="muted">
                Clear all filters
              </Text>
              {/* The chord only while this panel owns it — a panel under a
                  dialog's must not advertise a key it won't get. */}
              {isHotkeyTarget ? (
                <MenuShortcut>{CLEAR_FILTERS_SHORTCUT_LABEL}</MenuShortcut>
              ) : null}
            </MenuItem>
          </Box>
        ) : null}
      </MenuPopup>
      </Menu>
    </>
  );
}
